import React, { useEffect } from "react";
import { CacheProvider } from "@emotion/react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/redux/store";
import Head from "next/head";
import createEmotionCache from "@/utils/createEmotionCache";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/globals.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { RumErrorEvent } from "@datadog/browser-rum";
import "../components/LoadingSpinner/LoadingSpinner.module.css";
import ChatBot from "../components/ChatBot/ChatBot";
import DatadogUserWrapper from "@/hooks/useDatadogParentUser";
// import OldChatBot from "../components/OldChatBot/OldChatBot";

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
 
datadogRum.init({
  applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID,
  clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  site: process.env.NEXT_PUBLIC_DATADOG_SITE,
  service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
  env: process.env.NEXT_PUBLIC_WEB_ENV,
  version: "1.0.0", // The version you release
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  //allowedTracingUrls: [process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URLS],

  allowedTracingUrls: [
    (url) => url.startsWith(process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URLS),
    (url) => url.startsWith(process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URL_EDUCATION),
    (url) => url.startsWith(process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URL_INFOSERVICE),
    (url) => url.startsWith(process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URL_KNOWLEDGE),
    (url) => url.startsWith(process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URL_ACADEMY),
    (url) => url.startsWith(process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACKING_URL_ADMISSION)
  ],

  defaultPrivacyLevel: "mask-user-input",

  beforeSend: (event) => {
    if (event.error) {
      const error = (event as RumErrorEvent).error;
      // filter out error that might be caused by link validation/ pre-fetch rendering from email clicks
      // https://github.com/DataDog/browser-sdk/issues/2715
      const regex =
        /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
      if (regex.test(error.message)) return false; // Filter this error out
    }

    return true; // Otherwise, send the event
  },
});
 
// Add Datadog RUM log initialization
datadogLogs.init({
  clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  site: process.env.NEXT_PUBLIC_DATADOG_SITE,
  service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
  env: process.env.NEXT_PUBLIC_WEB_ENV,
  version: "1.0.0", // The version you release
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
});

datadogRum.setGlobalContext({
  product: 'aldar-education'
});
 
function MyApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}) {
  useEffect(() => {
    import("bootstrap/dist/js/bootstrap.bundle.min.js");
    import("slick-carousel/slick/slick.js");
  }, []);
 
  return (
    <>
      <CacheProvider value={emotionCache}>
        <ErrorBoundary>
          <Provider store={store}>
          <DatadogUserWrapper />
            <PersistGate loading={null} persistor={persistor}>
              <Head>
                <meta
                  name="referrer"
                  content="strict-origin-when-cross-origin"
                />
              </Head>
              <Component {...pageProps} />
              <ToastContainer />
              <ChatBot />
              {/* <OldChatBot /> */}
            </PersistGate>
          </Provider>
        </ErrorBoundary>
      </CacheProvider>
    </>
  );
}
 
export default MyApp;